import { useState } from "react";
import { useAuth } from "../../context/AuthProvider";
import { useAccountLoginUrl } from "../../hooks/useAccountLoginUrl";
import { clearPromotedHint } from "../../lib/anonSession";
import { AccountNudgeBanner } from "./AccountNudgeBanner";

/**
 * Promoted-session banner (Phase B / lane B2).
 *
 * Rendered when this device is ANON but its presented credential resolves to a
 * session already PROMOTED to a Fuel Goods account (server flags
 * `promotedToAccount`; AuthProvider persists a durable `fg.promotedHint` and
 * exposes it as `state.promotedHint`). The shopper's plan/data lives on their
 * account — nudge them to sign in to see it, rather than treating them as a
 * fresh guest.
 *
 * CTA is a same-tab navigation to the storefront login URL (`useAccountLoginUrl`
 * honors the deployment's account model). Dismiss clears the hint for good
 * (`clearPromotedHint`) so it doesn't re-arm on the next context refresh. Mirrors
 * `<ReconcileRetryBanner>`: reads auth state, renders nothing unless the anon
 * session carries a live promoted hint.
 *
 * Shares its markup/styling with `<SignedOutBanner>` via `<AccountNudgeBanner>`
 * (E1 consolidation) — the two states are mutually exclusive at the source
 * (`promotedHint` wins over `signedOut`), so the banners never stack.
 */
export function PromotedSessionBanner() {
  const { state } = useAuth();
  const loginUrl = useAccountLoginUrl();
  const [dismissed, setDismissed] = useState(false);

  if (state.kind !== "anon" || !state.promotedHint || dismissed) {
    return null;
  }

  const onDismiss = () => {
    clearPromotedHint();
    setDismissed(true);
  };

  return (
    <AccountNudgeBanner
      icon="check"
      message="Your plan is saved to your Fuel Goods account."
      ctaLabel="Sign in to view it"
      loginUrl={loginUrl}
      onDismiss={onDismiss}
    />
  );
}
